import { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pagination } from "./AdminUsers";
import { Undo2, Search } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["", "paid", "unpaid", "initiated", "failed", "expired", "refunded"];

export default function AdminPayments() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ revenue_total: 0, refunded_count: 0, failed_count: 0 });
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 25;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/payments", { params: { status_filter: statusFilter, q, skip, limit } });
      setRows(data.rows); setTotal(data.total); setStats(data.stats);
    } catch (e) {}
  }, [statusFilter, q, skip]);
  useEffect(() => { load(); }, [load]);

  const refund = async (id) => {
    const reason = prompt("Refund reason (required):") || "";
    if (!reason) return;
    try {
      await api.post(`/admin/payments/${id}/refund`, { reason });
      toast.success("Refund recorded · downgrade applied. NOTE: live Stripe API refund is MOCKED until production keys are connected.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <AdminLayout subtitle="Module 11 · Commerce" title="Payments & Refunds" actions={<div className="text-xs font-mono-plex text-zinc-500">{total.toLocaleString()} transactions</div>}>
      <div className="grid grid-cols-3 gap-px bg-white/10 mb-8" data-testid="payments-kpis">
        <Kpi label="Total revenue" value={`$${stats.revenue_total.toFixed(2)}`} accent="border-l-ec-emerald" testid="pay-kpi-revenue" />
        <Kpi label="Refunded" value={stats.refunded_count} accent="border-l-amber-500" testid="pay-kpi-refunded" />
        <Kpi label="Failed / expired" value={stats.failed_count} accent="border-l-ec-error" testid="pay-kpi-failed" />
      </div>

      <div className="flex flex-wrap gap-3 mb-5" data-testid="payments-filters">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email, plan slug, session ID, coupon…" className="rounded-sm h-9 pl-9 bg-white/5 border-white/10 text-white" data-testid="payments-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-sm h-9 px-3 bg-white/5 border border-white/10 text-white text-sm" data-testid="payments-status-filter">
          {STATUSES.map((s) => <option key={s} value={s} className="bg-ec-ink">{s || "All statuses"}</option>)}
        </select>
      </div>

      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="payments-table">
            <thead className="bg-white/5">
              <tr className="text-left">
                <th className="px-4 py-3 eyebrow text-zinc-500">When</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">User</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Plan</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Amount</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Coupon</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Provider</th>
                <th className="px-4 py-3 eyebrow text-zinc-500">Status</th>
                <th className="px-4 py-3 eyebrow text-zinc-500 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((tx) => (
                <tr key={tx.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`pay-row-${tx.id}`}>
                  <td className="px-4 py-3 text-xs font-mono-plex text-zinc-400">{tx.created_at?.slice(0, 16)}</td>
                  <td className="px-4 py-3 text-xs">{tx.user_email}</td>
                  <td className="px-4 py-3 text-xs font-mono-plex text-zinc-300">{tx.plan_slug}</td>
                  <td className="px-4 py-3 font-display font-bold text-white">${tx.amount?.toFixed(2)}<span className="text-[10px] font-mono-plex font-normal text-zinc-500 ml-1">{tx.currency?.toUpperCase()}</span></td>
                  <td className="px-4 py-3 text-xs font-mono-plex text-zinc-400">{tx.coupon_code || "—"}</td>
                  <td className="px-4 py-3"><Badge className="bg-white/10 border-white/10 rounded-sm">{tx.provider}</Badge></td>
                  <td className="px-4 py-3"><StatusBadge status={tx.payment_status} /></td>
                  <td className="px-4 py-3 text-right">
                    {tx.payment_status === "paid" && (
                      <Button size="sm" variant="outline" onClick={() => refund(tx.id)} className="rounded-sm h-7 border-amber-500/40 text-amber-400 hover:bg-amber-500/10 gap-1" data-testid={`pay-refund-${tx.id}`}>
                        <Undo2 className="w-3 h-3" />Refund
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (<tr><td colSpan={8} className="p-10 text-center text-zinc-500" data-testid="payments-empty">No transactions.</td></tr>)}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Pagination total={total} skip={skip} limit={limit} setSkip={setSkip} />
    </AdminLayout>
  );
}

function Kpi({ label, value, accent, testid }) {
  return (
    <div className={`p-5 bg-ec-ink border-l-2 ${accent}`} data-testid={testid}>
      <div className="eyebrow text-zinc-500">{label}</div>
      <div className="font-display text-3xl font-black mt-2 text-white">{value}</div>
    </div>
  );
}
function StatusBadge({ status }) {
  const cls = status === "paid" ? "bg-ec-emerald/10 text-ec-emerald border-ec-emerald/40"
    : status === "refunded" ? "bg-amber-500/10 text-amber-400 border-amber-500/40"
    : status === "failed" || status === "expired" ? "bg-ec-error/20 text-ec-error border-ec-error/40"
    : "bg-white/10 text-zinc-300 border-white/10";
  return <Badge className={`rounded-sm border ${cls}`}>{status || "—"}</Badge>;
}
